import React, { useState } from 'react';
import { Users, MessageSquare, FileText, Calendar, Video, Search, Plus } from 'lucide-react';
import { ContractSpace } from './collaboration/ContractSpace';
import { MessagingCenter } from './collaboration/MessagingCenter';
import { DocumentCenter } from './collaboration/DocumentCenter';
import { MeetingCenter } from './collaboration/MeetingCenter';
import { useNavigate, useLocation } from 'react-router-dom';

export function CollaborationTools() {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(() => {
    // Get tab from URL or default to 'contracts'
    const params = new URLSearchParams(location.search);
    return params.get('tab') || 'contracts';
  });
  const navigate = useNavigate();

  const tabs = [
    { id: 'contracts', name: 'Contract Spaces', icon: Users },
    { id: 'messages', name: 'Messages', icon: MessageSquare },
    { id: 'documents', name: 'Documents', icon: FileText },
    { id: 'meetings', name: 'Meetings', icon: Video },
  ];

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    // Update URL with selected tab
    navigate(`/marketplace/collaboration?tab=${tabId}`, { replace: true });
  };

  const handleNewItem = () => {
    switch (activeTab) {
      case 'contracts':
        navigate('/contracts/new');
        break;
      case 'messages':
        // Handle new message
        break;
      case 'documents':
        // Handle new document
        break;
      case 'meetings':
        // Handle new meeting
        break;
    }
  };

  return (
    <div className="min-h-screen space-y-6 bg-gray-50 pb-12">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <h1 className="text-2xl font-semibold text-gray-900">Collaboration Center</h1>
            <p className="mt-1 text-sm text-gray-500">
              Manage your contracts, messages, documents, and meetings in one place
            </p>
          </div>

          {/* Search and Actions */}
          <div className="flex items-center justify-between border-b border-gray-200 py-4">
            <div className="flex-1 pr-4">
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder={`Search ${activeTab}...`}
                  className="w-full rounded-lg border border-gray-300 py-2 pl-10 pr-4 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
              </div>
            </div>
            <button
              onClick={handleNewItem}
              className="inline-flex items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
              <Plus className="mr-2 h-4 w-4" />
              New {activeTab.slice(0, -1)}
            </button>
          </div>

          {/* Tabs */}
          <div className="flex space-x-8">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`group relative flex items-center space-x-2 border-b-2 px-1 pb-4 pt-4 text-sm font-medium transition-colors ${
                    activeTab === tab.id
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <span>{tab.name}</span>
                  {tab.id === 'messages' && (
                    <span className="ml-2 rounded-full bg-indigo-100 px-2.5 py-0.5 text-xs font-medium text-indigo-800">
                      5
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="rounded-lg bg-white p-6 shadow">
          {activeTab === 'contracts' && <ContractSpace />}
          {activeTab === 'messages' && <MessagingCenter contractId="1" />}
          {activeTab === 'documents' && <DocumentCenter contractId="1" />}
          {activeTab === 'meetings' && <MeetingCenter contractId="1" />}
        </div>
      </div>
    </div>
  );
}